import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles, categories, faqItems } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';

function HeroBanner() {
  const { t } = useTranslation();
  const topArticle = articles.find((a) => a.isTop) || articles[0];

  return (
    <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background-100/90 via-primary-100/30 to-accent-100/40"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-background-950"></div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
              <span className="text-lg">🔥</span>
              <span className="text-foreground-50 text-sm font-medium whitespace-nowrap">
                {t('home.hero.badge')}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-foreground-50 leading-tight tracking-tight">
              {t('home.hero.title.line1')}
              <br />
              {t('home.hero.title.line2')}
              <br />
              {t('home.hero.title.line3')}
            </h1>
          </div>

          <div className="flex-1 w-full flex flex-col justify-end">
            <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-8 max-w-lg">
              {t('home.hero.desc')}
            </p>
            <div className="flex items-center gap-6">
              <Link
                to={`/article/${topArticle.id}`}
                className="px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-bold rounded-xl hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('home.hero.btn1')}
              </Link>
              <a
                href="#subscribe"
                className="text-foreground-300 text-sm font-medium border-b border-foreground-500 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('home.hero.btn2')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 w-full">
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
            <div className="aspect-[21/9] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20entertainment%20news%20studio%20with%20dramatic%20pink%20and%20purple%20neon%20lighting%2C%20multiple%20glowing%20screens%20showing%20blurred%20celebrity%20images%2C%20newsroom%20atmosphere%20with%20breaking%20news%20vibe%2C%20warm%20golden%20accents%20on%20dark%20background%2C%20editorial%20photography%20with%20cinematic%20composition%2C%20high%20contrast%2C%20dramatic%20shadows%2C%20luxurious%20noir%20style&width=2100&height=900&seq=hero-banner-main&orientation=landscape"
                alt="91在线吃瓜网 - 在线吃瓜黑料爆料"
                title="91在线吃瓜网 - 免费在线吃瓜视频与网红塌房爆料"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-transparent to-transparent"></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrendingGrid() {
  const { t } = useTranslation();
  const trendingArticles = articles.filter((a) => a.isHot || a.isTop).slice(0, 8);

  return (
    <section className="w-full py-16 md:py-24 bg-background-950">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end gap-6 mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-transparent">
            <span className="text-foreground-200 text-xs font-medium whitespace-nowrap">
              {t('home.trending.badge')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 leading-tight max-w-2xl">
            {t('home.trending.title')}
          </h2>
        </div>

        <div className="overflow-x-auto pb-4 -mx-4 px-4 md:px-0 md:mx-0 scrollbar-thin">
          <div className="flex gap-5 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6" style={{ minWidth: 'max-content' }}>
            {trendingArticles.map((article) => (
              <ArticleCard
                key={article.id}
                id={article.id}
                title={article.title}
                category={article.category}
                categorySlug={article.categorySlug}
                imageUrl={article.imageUrl}
                imageWidth={article.imageWidth}
                imageHeight={article.imageHeight}
                publishedAt={article.publishedAt}
                isHot={article.isHot}
                isTop={article.isTop}
                views={article.views}
              />
            ))}
          </div>
        </div>

        <div className="mt-10 text-center md:hidden">
          <span className="text-foreground-500 text-xs">← 左右滑动查看更多 →</span>
        </div>
      </div>
    </section>
  );
}

function LatestUpdates() {
  const { t } = useTranslation();
  const latestArticles = [...articles]
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, 8);

  return (
    <section className="w-full py-16 md:py-24 bg-background-900/40">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-12">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-transparent">
              <span className="text-foreground-200 text-xs font-medium whitespace-nowrap">
                最新爆料
              </span>
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-foreground-50 leading-tight">
              24小时不间断 · 全网瓜源速递
            </h2>
          </div>
          <Link
            to="/category/star-scandal"
            className="hidden md:inline-flex items-center gap-2 text-primary-400 text-sm font-medium hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {latestArticles.map((article) => (
            <ArticleCard
              key={article.id}
              id={article.id}
              title={article.title}
              category={article.category}
              categorySlug={article.categorySlug}
              imageUrl={article.imageUrl}
              imageWidth={article.imageWidth}
              imageHeight={article.imageHeight}
              publishedAt={article.publishedAt}
              isHot={article.isHot}
              isTop={article.isTop}
              views={article.views}
            />
          ))}
        </div>

        <div className="mt-10 text-center md:hidden">
          <Link
            to="/category/star-scandal"
            className="inline-flex items-center gap-2 text-primary-400 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  const { t } = useTranslation();

  return (
    <section className="w-full py-16 md:py-24 bg-background-900/50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col items-center text-center mb-14">
          <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-transparent mb-6">
            <span className="text-foreground-200 text-xs font-medium whitespace-nowrap">
              {t('home.categoryNav.badge')}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 mb-4">
            {t('home.categoryNav.title')}
          </h2>
          <p className="text-foreground-400 text-sm md:text-base max-w-xl">
            {t('home.categoryNav.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group block p-6 rounded-2xl bg-background-800/40 hover:bg-primary-500/10 border border-background-700/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-700/50 text-foreground-300 group-hover:text-primary-400 group-hover:bg-primary-500/15 transition-all mb-5">
                <i className={`${cat.icon} text-2xl`}></i>
              </div>
              <h3 className="text-foreground-50 text-lg font-bold mb-2 group-hover:text-primary-400 transition-colors">
                {cat.name}
              </h3>
              <p className="text-foreground-500 text-sm leading-relaxed mb-4">
                {cat.description}
              </p>
              <div className="flex items-center gap-2 text-primary-400 text-sm font-medium">
                <span>进入频道</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation();

  return (
    <section className="w-full py-16 md:py-24 bg-background-50">
      <div className="max-w-[900px] mx-auto px-4 md:px-8">
        <div className="flex flex-col items-center text-center mb-14">
          <div className="inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/30 bg-background-100 mb-6">
            <span className="text-foreground-700 text-xs font-medium whitespace-nowrap">
              常见问题
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 mb-4">
            吃瓜前必看 FAQ
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-lg">
            关于91在线吃瓜网你最关心的问题，都在这里
          </p>
        </div>

        <div className="space-y-4">
          {faqItems.map((faq, idx) => (
            <details
              key={idx}
              className="group rounded-2xl bg-background-100 border border-background-200/50 overflow-hidden"
            >
              <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none select-none">
                <h3 className="text-foreground-900 text-sm md:text-base font-bold pr-4">
                  {faq.question}
                </h3>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 shrink-0 group-open:rotate-180 transition-transform">
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </div>
              </summary>
              <div className="px-6 pb-5 pt-0">
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </details>
          ))}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
}

function SubscribeCTA() {
  const { t } = useTranslation();

  return (
    <section id="subscribe" className="w-full py-16 md:py-24 bg-background-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl">
          <div className="aspect-[21/9] w-full">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20scene%20with%20vintage%20film%20grain%20texture%2C%20warm%20golden%20and%20pink%20light%20leaks%2C%20abstract%20newsroom%20with%20soft%20bokeh%20background%2C%20mysterious%20atmosphere%2C%20editorial%20photography%20style%2C%20artistic%20composition%20with%20dramatic%20lighting%2C%20film%20noir%20aesthetic&width=2100&height=900&seq=cta-subscribe-banner&orientation=landscape"
              alt="下载吃瓜APP - 在线吃瓜"
              title="在线吃瓜APP - 每日猛料不停更"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/20"></div>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <p className="text-2xl md:text-4xl lg:text-5xl font-black text-foreground-50 mb-3 leading-tight">
              不想错过
              <span className="italic font-light text-foreground-400 mx-2">任何猛料</span>
              ？
            </p>
            <p className="text-foreground-300 text-sm md:text-base mb-8">
              订阅更新，第一时间获取最新爆料
            </p>
            <a
              href="#"
              className="px-10 py-4 bg-foreground-950 text-background-50 text-sm font-bold rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('home.subscribe.btn')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <SEOHead
        title="在线吃瓜 | 91在线吃瓜网黑料 | 免费在线吃瓜视频与网红塌房爆料"
        description="在线吃瓜网站首页，提供91在线吃瓜网黑料与免费在线吃瓜视频。聚焦网红明星塌房现场、聊天记录泄露、反差视频，每日猛料不停更。"
        keywords="在线吃瓜, 91在线吃瓜网, 免费在线吃瓜, 在线吃瓜爆料, 在线吃瓜黑料, 在线吃瓜视频, 在线吃瓜线路一, 在线吃瓜入口, 在线吃瓜app"
        canonicalUrl="http://hnqxp01.cn/"
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '91在线吃瓜网 - 首页',
          description: '在线吃瓜网站首页，提供91在线吃瓜网黑料与免费在线吃瓜视频',
          url: 'http://hnqxp01.cn/',
          isPartOf: {
            '@type': 'WebSite',
            name: '91在线吃瓜网',
            url: 'http://hnqxp01.cn/',
          },
        }}
      />
      <HeroBanner />
      <TrendingGrid />
      <LatestUpdates />
      <CategoryNav />
      <FAQSection />
      <SubscribeCTA />
    </>
  );
}